// 2.9.8 (Owner): a description opens from the question mark beside its heading, always above it, so it never covers
// what it describes. A mouse opens it on hover and it shrinks back into the mark when the pointer leaves; a touch
// toggles it and a touch anywhere else closes it. Keyboard focus on the heading shows it too.
// 2.9.9 (Owner): add-on groups and campaigns share this window. A <meri-help-window> pairs with the [data-help-mark]
// in its parent element. A mark inside a <summary> leaves focus to the summary, which also folds its group; any other
// mark is a button that takes focus itself and reports whether its window is open.
// 2.9.9 (Owner, later): an add-on's own description opens from a mark beside its name, inside the choice's <label>.
// The choice keeps focus and is described by the window, and pressing the mark never selects the choice.
// 2026-11-04 (Owner): the window stands up out of its mark in depth and settles on the theme's spring (popover.js).
import { growWindow, shrinkWindow } from './popover.js?v=20261104-motion';

const Base = globalThis.HTMLElement || class {};
let count = 0;
// An add-on's product description waits in a <template>, so nothing in it loads with the page. The window takes it in
// without videos, embeds or form controls, which neither play nor count in the small window.
const LEFT_OUT =
  'iframe, frame, video, audio, embed, object, script, style, link, meta, form, input, select, textarea, button';

// Shopify's CDN scales a picture to its width parameter, never beyond the original; 800px stays sharp across the
// window (at most 24rem) on a high-density screen.
function fitPicture(image) {
  if (image.hasAttribute('srcset')) return;
  try {
    const url = new URL(image.getAttribute('src'), location.href);
    if (!/\.(jpe?g|png|webp)$/i.test(url.pathname)) return;
    if (url.hostname !== 'cdn.shopify.com' && !url.pathname.startsWith('/cdn/shop/')) return;
    if (url.searchParams.has('width') || url.searchParams.has('height')) return;
    url.searchParams.set('width', '800');
    image.src = url.href;
  } catch {
    // A picture without a usable address stays as written.
  }
}

function takeIn(panel) {
  const source = panel.querySelector('template[data-help-content]');
  if (!source) return;
  const content = source.content.cloneNode(true);
  for (const node of content.querySelectorAll(LEFT_OUT)) node.remove();
  // Pictures load when the window first shows.
  for (const image of content.querySelectorAll('img')) {
    image.loading = 'lazy';
    image.decoding = 'async';
    fitPicture(image);
  }
  source.replaceWith(content);
}

function helpWindow(icon, panel, signal) {
  const body = panel.querySelector('.options-help-body');
  const summary = icon.closest('summary');
  // The choice's own control sits inside its label, so a repeated section never points the mark at another copy.
  const choice = summary ? null : icon.closest('label');
  const control = choice?.querySelector('input, select, textarea') || choice?.control || null;
  const owner = summary || control || icon;
  const button = owner === icon;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let pointer = 'mouse';
  let timer = 0;
  let animation = null;
  let closing = false;
  let ownScroll = false;
  let following = 0;
  let watch = null;
  let hovering = false;
  let onPanel = false;
  let spot = [0, 0];
  let resting = 0;
  let waiting = null;
  const expanded = (value) => button && icon.setAttribute('aria-expanded', String(value));
  // Keyboard focus may scroll its heading or choice into view, gliding for a moment; until the page rests, the window
  // follows its mark instead of closing.
  const follow = () => {
    clearTimeout(following);
    following = setTimeout(() => (following = 0), 200);
  };
  if (button) {
    panel.id ||= `meri-help-${++count}`;
    icon.setAttribute('aria-controls', panel.id);
    expanded(false);
  }
  const isOpen = () => panel.matches(':popover-open');
  const place = (makeRoom) => {
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft || 0;
    const top = viewport?.offsetTop || 0;
    const width = viewport?.width || innerWidth;
    const height = viewport?.height || innerHeight;
    body.style.removeProperty('--options-help-max');
    const natural = panel.offsetHeight;
    const frame = natural - body.offsetHeight;
    // 2.9.9 (Owner): the window is at most about half the screen; longer text scrolls inside it behind the
    // theme's up and down arrows, as in the region and language list (theme.js HINTED_LISTS).
    const tallest = Math.min(natural, Math.round(height / 2));
    // 12px above the mark for the arrow and 12px from the edge of the screen.
    let rect = icon.getBoundingClientRect();
    let room = rect.top - top - 24;
    if (makeRoom && room < tallest && scrollY > 0) {
      // A touch near the top of the screen moves the page down just enough for the window to fit above the mark.
      ownScroll = true;
      scrollBy({ top: -Math.min(scrollY, tallest - room), behavior: 'instant' });
      requestAnimationFrame(() => (ownScroll = false));
      rect = icon.getBoundingClientRect();
      room = rect.top - top - 24;
    }
    body.style.setProperty('--options-help-max', Math.max(48, Math.min(room, tallest) - frame) + 'px');
    const centre = rect.left + rect.width / 2;
    const x = Math.max(left + 12, Math.min(centre - 24, left + width - panel.offsetWidth - 12));
    panel.style.left = x + 'px';
    panel.style.top = rect.top - 12 - panel.offsetHeight + 'px';
    panel.style.setProperty('--options-help-arrow', Math.max(16, Math.min(panel.offsetWidth - 16, centre - x)) + 'px');
  };
  const open = (makeRoom = false) => {
    clearTimeout(timer);
    timer = 0;
    if (isOpen() && !closing) return;
    animation?.cancel();
    closing = false;
    if (!isOpen()) panel.showPopover();
    place(makeRoom);
    icon.toggleAttribute('data-open', true);
    expanded(true);
    // The window always sits above its mark.
    animation = growWindow(panel, 'above');
  };
  const close = (now = false) => {
    clearTimeout(timer);
    timer = 0;
    if (!isOpen() || (closing && !now)) return;
    icon.removeAttribute('data-open');
    expanded(false);
    animation?.cancel();
    if (now || reduced.matches) {
      closing = false;
      panel.hidePopover();
      return;
    }
    closing = true;
    animation = shrinkWindow(panel);
    animation.onfinish = () => {
      closing = false;
      animation.cancel();
      panel.hidePopover();
    };
  };
  const later = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = 0;
      close();
    }, 220);
  };
  // 2.9.9 (Owner): a mark reached while the page still glides (the theme's buffered wheel scroll) opened a window
  // that the next scroll frame closed, so it took a second hover, and waiting for the glide felt slow. A pointer that
  // moves onto the mark opens the window at once; while the pointer stays on the mark or the window, the page's
  // scroll moves the window with its mark instead of closing it. A mark that the glide only carries under a still
  // pointer (the browser then reports no pointer movement) waits, so the windows of marks gliding past never flash:
  // it opens once the glide has ended, nothing around the mark has scrolled for 150 ms and the pointer is still on
  // it. The glide's last frames move less than a pixel, so a still frame or two does not mean the page has stopped.
  const onMark = () => {
    const hit = document.elementFromPoint(spot[0], spot[1]);
    return Boolean(hit && icon.contains(hit));
  };
  const stopWaiting = () => {
    clearTimeout(resting);
    resting = 0;
    waiting?.abort();
    waiting = null;
  };
  const openAtRest = () => {
    stopWaiting();
    waiting = new AbortController();
    const wait = () => {
      clearTimeout(resting);
      resting = setTimeout(() => {
        // The buffered page scroll marks the page until its glide has ended.
        if (document.documentElement.classList.contains('lenis-smooth')) return wait();
        stopWaiting();
        if (hovering && onMark()) open();
      }, 150);
    };
    document.addEventListener('scroll', (scroll) => (scroll.target.contains?.(icon) ?? true) && wait(), {
      capture: true,
      passive: true,
      signal: waiting.signal,
    });
    wait();
  };
  // Page-wide listeners exist only while the window is open, or while a pointer waits on its mark for the page to rest.
  signal.addEventListener(
    'abort',
    () => {
      clearTimeout(timer);
      timer = 0;
      clearTimeout(following);
      stopWaiting();
      animation?.cancel();
      watch?.abort();
      icon.removeAttribute('data-open');
      expanded(false);
    },
    { once: true },
  );
  panel.addEventListener(
    'beforetoggle',
    (event) => {
      watch?.abort();
      watch = null;
      if (event.newState !== 'open' || signal.aborted) return;
      watch = new AbortController();
      const options = { signal: watch.signal };
      document.addEventListener(
        'pointerdown',
        (down) => {
          if (!panel.contains(down.target) && !icon.contains(down.target)) close();
        },
        { ...options, capture: true },
      );
      document.addEventListener(
        'keydown',
        (key) => {
          if (key.key !== 'Escape') return;
          if (panel.contains(document.activeElement)) owner.focus({ preventScroll: true });
          close();
        },
        options,
      );
      document.addEventListener(
        'scroll',
        (scroll) => {
          if (ownScroll || panel.contains(scroll.target)) return;
          if (following) {
            follow();
            place(false);
            return;
          }
          if (hovering && onMark()) {
            place(false);
            return;
          }
          // A pointer on the window, or one that has just left the mark or the window, keeps it for now.
          if (onPanel || timer) return;
          close(true);
          if (hovering) openAtRest();
        },
        { ...options, capture: true, passive: true },
      );
      window.addEventListener('resize', () => close(true), { ...options, passive: true });
      // 2.9.9 (Owner): inside the window the wheel and a finger scroll only its text, also over its edges and
      // arrow, and never carry on to the page; outside it the page scrolls as before and the window closes.
      const room = (dy) =>
        dy < 0 ? body.scrollTop > 0 : dy > 0 && body.scrollTop + body.clientHeight < body.scrollHeight - 1;
      panel.addEventListener(
        'wheel',
        (wheel) => {
          if (wheel.ctrlKey) return;
          const inside = body.contains(wheel.target);
          if (inside && room(wheel.deltaY)) return;
          wheel.preventDefault();
          const unit = wheel.deltaMode === 1 ? 16 : wheel.deltaMode === 2 ? body.clientHeight : 1;
          if (!inside && room(wheel.deltaY)) body.scrollBy({ top: wheel.deltaY * unit, behavior: 'instant' });
        },
        { ...options, passive: false },
      );
      let startY = 0;
      panel.addEventListener('touchstart', (touch) => (startY = touch.touches[0].clientY), {
        ...options,
        passive: true,
      });
      panel.addEventListener(
        'touchmove',
        (touch) => {
          if (body.contains(touch.target) && room(startY - touch.touches[0].clientY)) return;
          if (touch.cancelable) touch.preventDefault();
        },
        { ...options, passive: false },
      );
    },
    { signal },
  );
  icon.addEventListener('pointerdown', (event) => (pointer = event.pointerType || 'mouse'), { signal });
  // A press on an add-on's mark leaves focus where it was, so a chosen add-on keeps focus and its window stays open.
  if (control) icon.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
  icon.addEventListener(
    'pointerenter',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      hovering = true;
      spot = [event.clientX, event.clientY];
      // The buffered page scroll marks the page while it glides or has just been scrolled.
      if (document.documentElement.classList.contains('lenis-scrolling')) openAtRest();
      else open();
    },
    { signal },
  );
  icon.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      spot = [event.clientX, event.clientY];
      // A pointer that really moves onto the mark opens it at once, also while the page glides.
      if (!isOpen() || closing) {
        stopWaiting();
        open();
      }
    },
    { signal },
  );
  icon.addEventListener(
    'pointerleave',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      hovering = false;
      stopWaiting();
      // A still pointer means the page carried the mark away: its window goes at once, as on any page scroll. A
      // pointer that moves off the mark has a moment to reach the window.
      if (event.clientX === spot[0] && event.clientY === spot[1]) close(true);
      else later();
    },
    { signal },
  );
  panel.addEventListener(
    'pointerenter',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      onPanel = true;
      clearTimeout(timer);
      timer = 0;
    },
    { signal },
  );
  panel.addEventListener(
    'pointerleave',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      onPanel = false;
      later();
    },
    { signal },
  );
  // A picture that arrives while the window is open grows it; the window moves up so it stays above the mark.
  panel.addEventListener('load', () => isOpen() && !closing && place(false), { capture: true, signal });
  icon.addEventListener(
    'click',
    (event) => {
      // The mark sits inside the heading or the choice; touching it must not fold the group or select the choice.
      event.preventDefault();
      if (button && event.detail === 0) {
        // Enter or Space on a button mark toggles its window, like any disclosure button.
        if (isOpen() && !closing) close();
        else open(true);
      } else if (pointer === 'mouse') open();
      else if (isOpen() && !closing) close();
      else open(true);
    },
    { signal },
  );
  owner.addEventListener(
    'focus',
    () => {
      if (!owner.matches(':focus-visible')) return;
      open(true);
      follow();
    },
    { signal },
  );
  const leave = (event) => {
    if (!panel.contains(event.relatedTarget) && event.relatedTarget !== owner) close();
  };
  owner.addEventListener('blur', leave, { signal });
  panel.addEventListener('focusout', leave, { signal });
  // An add-on group's window sits after its choices in the DOM. Visit its links from the heading, then return to
  // the choices; a campaign's window follows its mark, so the same steps keep the natural order.
  const links = () => [...panel.querySelectorAll('a[href]')];
  const focusLink = (link) => {
    link.focus({ preventScroll: true });
    const text = body.getBoundingClientRect();
    const target = link.getBoundingClientRect();
    // Keep keyboard focus visible in long descriptions without moving the page or the window above its mark.
    if (target.top < text.top + 4 || target.bottom > text.bottom - 4)
      body.scrollBy({ top: (target.top + target.bottom - text.top - text.bottom) / 2, behavior: 'instant' });
  };
  owner.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Tab' || event.shiftKey || !isOpen()) return;
      const first = links()[0];
      if (!first) return;
      event.preventDefault();
      focusLink(first);
    },
    { signal },
  );
  panel.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Tab') return;
      const items = links();
      const next = items[items.indexOf(event.target) + (event.shiftKey ? -1 : 1)];
      if (next) {
        event.preventDefault();
        focusLink(next);
      } else if (event.shiftKey && event.target === items[0]) {
        event.preventDefault();
        owner.focus({ preventScroll: true });
      } else if (!event.shiftKey && event.target === items.at(-1)) {
        owner.focus({ preventScroll: true });
        close(true);
        // Native Tab now proceeds from the heading past the closed window, without skipping what follows.
      }
    },
    { signal },
  );
  return close;
}

export class MeriHelpWindow extends Base {
  connectedCallback() {
    this.abort?.abort();
    takeIn(this);
    const mark = this.parentElement?.querySelector('[data-help-mark]');
    if (!mark || !('showPopover' in HTMLElement.prototype)) return;
    this.abort = new AbortController();
    this.closer = helpWindow(mark, this, this.abort.signal);
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.abort = this.closer = null;
  }
  // An add-on group closes its window at once when the group folds.
  close(now = false) {
    this.closer?.(now);
  }
}

if (globalThis.customElements && !customElements.get('meri-help-window'))
  customElements.define('meri-help-window', MeriHelpWindow);
